import { Alert, Box, LinearProgress, Typography } from '@mui/material';
import { useState } from 'react';
import { useNotify } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import constants from '../../constants';

// GH-695 (10.9.2026): mp3 upload for a podcast episode — same flow as the VOD video upload
// (multipart → server → S3 under podcasts/<id>/), plus the duration read from the file.
const readDuration = (file: File): Promise<number | null> =>
	new Promise((resolve) => {
		try {
			const url = URL.createObjectURL(file);
			const audio = document.createElement('audio');
			audio.preload = 'metadata';
			audio.onloadedmetadata = () => {
				URL.revokeObjectURL(url);
				resolve(Number.isFinite(audio.duration) ? Math.round(audio.duration) : null);
			};
			audio.onerror = () => resolve(null);
			audio.src = url;
		} catch {
			resolve(null);
		}
	});

const AudioUploadComponent = ({ podcastId }: { podcastId: string }) => {
	const [uploading, setUploading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [audioUrl, setAudioUrl] = useState<string | null>(null);
	const notify = useNotify();
	const { setValue } = useFormContext();

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;
		if (!file.type.startsWith('audio/') && !/\.(mp3|m4a|aac|wav)$/i.test(file.name)) {
			setError('רק קבצי אודיו (mp3) מותרים');
			return;
		}
		if (file.size > 200 * 1024 * 1024) {
			setError('הקובץ גדול מדי (מקסימום 200MB)');
			return;
		}
		setError(null);
		setUploading(true);
		setProgress(0);

		const duration = await readDuration(file);
		const token = localStorage.getItem('token');
		if (!token) {
			setError('לא נמצא טוקן אימות');
			setUploading(false);
			return;
		}
		const formData = new FormData();
		formData.append('audio', file);
		if (duration) formData.append('duration', String(duration));

		const xhr = new XMLHttpRequest();
		xhr.upload.addEventListener('progress', (e) => {
			if (e.lengthComputable) setProgress((e.loaded / e.total) * 100);
		});
		xhr.addEventListener('load', () => {
			setUploading(false);
			if (xhr.status === 200) {
				const data = JSON.parse(xhr.responseText);
				setAudioUrl(data.audioUrl);
				setValue('audioUrl', data.audioUrl);
				if (duration) setValue('duration', duration);
				notify('קובץ האודיו הועלה בהצלחה', { type: 'success' });
			} else {
				let message = 'שגיאה בהעלאת הקובץ';
				try {
					message = JSON.parse(xhr.responseText).message || message;
				} catch {}
				setError(message);
				notify(message, { type: 'error' });
			}
		});
		xhr.addEventListener('error', () => {
			setUploading(false);
			setError('שגיאת רשת בהעלאת הקובץ');
			notify('שגיאת רשת בהעלאת הקובץ', { type: 'error' });
		});
		xhr.open('POST', `${constants.API_URL}podcasts/${podcastId}/upload-audio`);
		xhr.setRequestHeader('Authorization', `Bearer ${token}`);
		xhr.send(formData);
	};

	return (
		<Box sx={{ mb: 2, width: '100%' }}>
			<Typography variant="body2" sx={{ mb: 1, direction: 'rtl' }}>
				העלאת קובץ אודיו (mp3)
			</Typography>
			<input type="file" accept="audio/*,.mp3,.m4a" onChange={handleFileChange} disabled={uploading} style={{ width: '100%', direction: 'rtl' }} />
			{uploading && (
				<Box sx={{ mt: 2 }}>
					<LinearProgress variant="determinate" value={progress} />
					<Typography variant="body2" sx={{ mt: 1, textAlign: 'right' }}>
						{Math.round(progress)}%
					</Typography>
				</Box>
			)}
			{error && (
				<Alert severity="error" sx={{ mt: 2, direction: 'rtl' }}>
					{error}
				</Alert>
			)}
			{audioUrl && (
				<Alert severity="success" sx={{ mt: 2, direction: 'rtl' }}>
					הקובץ הועלה: {audioUrl}
				</Alert>
			)}
		</Box>
	);
};

export default AudioUploadComponent;
